import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GrafanaIframeCard from '@app/components/GrafanaIframeCard';
import type { GrafanaIframeWidget } from '@app/components/GrafanaIframeCard';
import KpiCard from '@app/components/KpiCard';
import IncidentConversationPanel from '@app/components/IncidentConversationPanel';
import TopologyGraphRenderer from '@app/components/topology/TopologyGraphRenderer';
import { useDashboardsApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useGrafanaEmbedSessionKeepalive } from '@app/hooks/useGrafanaEmbedSessionKeepalive';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import type { SignalIncidentAnalysis, SignalIncidentDetail } from '@app/types/signals';
import type { DashboardWidgetSpec, TopologyV2ResolveResponse } from '@app/types/sdk';
import { TopologyV2ResolveResponseFromJSON } from '@reap/openapi-public-v1';
import { formatDateTime } from '@app/utils/format';
import { ApiResponseError, resolveBasePath } from '@app/utils/api';
import { buildOrgScopedPath } from '@app/utils/org-routes';
import {
  buildUnifiedIncidentHeadline,
  formatExternalTicketLabel,
  formatSignalSubtype,
  friendlyTargetLabel,
  safeExternalTicketHref,
  signalSeverityBadgeClass,
  signalStatusBadgeClass
} from '@app/utils/signals';
import { useConfig } from '@config/provider';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import type { UnifiedIncidentAttachment, UnifiedIncidentDetail, UnifiedIncidentExternalTicket } from './incidents/unifiedTypes';
import { shouldRedirectToCanonicalIncident } from './incidents/unifiedIncidentRoute';
import {
  ASSESSMENT_HEADING_MAP,
  ASSESSMENT_SECTION_TITLES,
  buildIncidentReferenceLine,
  type AssessmentSection,
  type AssessmentSectionKey,
  parseAssessmentDescription,
  stripIncidentDescriptionMetadata
} from './unifiedIncidentAssessment';

type UnifiedIncidentLocationState = {
  from?: string;
};

const UnifiedIncidentPage = () => {
  const orgId = useActiveOrgId();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as UnifiedIncidentLocationState | null) ?? null;
  const { incidentId } = useParams<{ incidentId: string }>();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource, fetchWithAuthRetry } = useAuthFetch();
  const dashboardsApi = useDashboardsApi();

  const decodedIncidentId = useMemo(() => {
    if (!incidentId) {
      return '';
    }
    try {
      return decodeURIComponent(incidentId);
    } catch {
      return incidentId;
    }
  }, [incidentId]);

  const detailFetcher = useMemo(() => {
    if (!orgId || !decodedIncidentId) {
      return null;
    }
    return () => fetchOrgResource<UnifiedIncidentDetail>(basePath, orgId, `incidents/${encodeURIComponent(decodedIncidentId)}`);
  }, [basePath, decodedIncidentId, fetchOrgResource, orgId]);

  const { data: incident, loading, error } = useApiQuery<UnifiedIncidentDetail>(detailFetcher, [detailFetcher]);

  useEffect(() => {
    const canonicalIncidentId = incident?.id?.trim();
    if (!orgId || !canonicalIncidentId || !shouldRedirectToCanonicalIncident(decodedIncidentId, canonicalIncidentId)) {
      return;
    }
    navigate(buildOrgScopedPath(`/app/incidents/${encodeURIComponent(canonicalIncidentId)}`, orgId, location.search, location.hash), {
      replace: true,
      state: state?.from ? { from: state.from } : undefined
    });
  }, [decodedIncidentId, incident?.id, location.hash, location.search, navigate, orgId, state?.from]);

  const signalDetailFetcher = useMemo(() => {
    if (!orgId || !decodedIncidentId) {
      return null;
    }
    return () => fetchOrgResource<SignalIncidentDetail>(basePath, orgId, `signals/incidents/${encodeURIComponent(decodedIncidentId)}`);
  }, [basePath, decodedIncidentId, fetchOrgResource, orgId]);
  const signalDetailQuery = useApiQuery<SignalIncidentDetail>(signalDetailFetcher, [signalDetailFetcher]);

  const topologyFetcher = useMemo(() => {
    if (!orgId || !signalDetailQuery.data?.topologyIntent?.viewId) {
      return null;
    }
    return async () => {
      const topologyIntent = signalDetailQuery.data?.topologyIntent;
      if (!topologyIntent?.viewId) {
        return null;
      }
      const requestBody = {
        scopeType: topologyIntent.scopeType ?? signalDetailQuery.data?.scopeType ?? 'site',
        scopeId: topologyIntent.scopeId ?? signalDetailQuery.data?.scopeId ?? '',
        params: topologyIntent.params ?? {},
        limits: topologyIntent.limits ?? {}
      };
      const url = `${basePath.replace(/\/$/, '')}/orgs/${encodeURIComponent(orgId)}/topology/views/${encodeURIComponent(
        topologyIntent.viewId
      )}/resolve`;
      const response = await fetchWithAuthRetry(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Topology resolve failed with status ${response.status}`);
      }
      const contentType = response.headers.get('Content-Type') ?? '';
      if (!contentType.toLowerCase().includes('application/json')) {
        throw new ApiResponseError(response, 'Expected JSON topology resolve response.');
      }
      return TopologyV2ResolveResponseFromJSON(await response.json());
    };
  }, [basePath, fetchWithAuthRetry, orgId, signalDetailQuery.data]);
  const topologyQuery = useApiQuery<TopologyV2ResolveResponse | null>(topologyFetcher, [topologyFetcher]);

  const handleBack = () => {
    if (state?.from) {
      navigate(state.from);
      return;
    }
    navigate(orgId ? buildOrgScopedPath('/app/incidents', orgId) : '/app/incidents');
  };

  const signalDetail = signalDetailQuery.data;
  const incidentSeverity = firstPresentText(signalDetail?.severity);
  const incidentStatus = firstPresentText(signalDetail?.status);
  const incidentSubtype = firstPresentText(signalDetail?.subtype, signalDetail?.conditionKey);
  const incidentTargetId = firstPresentText(signalDetail?.targetId);
  const targetLabel = friendlyTargetLabel(incidentTargetId || null, signalDetail?.attributes ?? null);
  const incidentReferenceLine = buildIncidentReferenceLine(incidentTargetId, incident?.displayId);
  const subtypeLabel = incidentSubtype ? formatSignalSubtype(incidentSubtype) : null;
  const telemetryWidgets = ((signalDetail?.telemetryIntent?.widgets ?? []).filter(
    (widget: DashboardWidgetSpec) => widget.type === 'grafana_panel_iframe' && Boolean(widget.iframeUrl)
  ) as GrafanaIframeWidget[]);
  const refreshGrafanaEmbedSession = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => dashboardsApi.orgsOrgIdGrafanaEmbedSessionPost({ orgId }, { credentials: 'include' }).then(() => undefined);
  }, [dashboardsApi, orgId]);

  useGrafanaEmbedSessionKeepalive({
    dashboardsApi,
    orgId,
    enabled: telemetryWidgets.length > 0,
    logTag: 'unified-incident'
  });

  const topologyGraph = topologyQuery.data?.graph ?? null;
  const topologyIntent = signalDetail?.topologyIntent ?? null;
  const topologyRootNodeId = useMemo(() => {
    const requestedRootNodeId = topologyIntent?.limits?.rootNodeId ?? signalDetail?.targetId ?? null;
    if (!requestedRootNodeId || !topologyGraph?.nodes?.some((node) => node.id === requestedRootNodeId)) {
      return null;
    }
    return requestedRootNodeId;
  }, [signalDetail?.targetId, topologyGraph?.nodes, topologyIntent?.limits?.rootNodeId]);
  const topologySelectedNodeIds = useMemo(
    () => (topologyRootNodeId ? new Set<string>([topologyRootNodeId]) : new Set<string>()),
    [topologyRootNodeId]
  );
  const topologyGraphGeneratedAt = topologyGraph?.generatedAt instanceof Date ? topologyGraph.generatedAt.toISOString() : '';
  const topologyGraphKey = [
    'unified-incident',
    decodedIncidentId,
    topologyIntent?.viewId ?? 'topology',
    topologyGraph?.graphId ?? 'graph',
    topologyGraphGeneratedAt,
    topologyRootNodeId ?? ''
  ].join(':');
  const signalDetailNotFound = getErrorStatus(signalDetailQuery.error) === 404;
  const signalContextAvailable = signalDetailQuery.loading || Boolean(signalDetail);

  if (!orgId) {
    return (
      <OrgModuleLayout title="Incident" description="Canonical incident detail across REAP and ticketing systems.">
        <OrgModulePlaceholder intent="Select an organization to load incident details." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="Incident"
      description={`Canonical incident detail for ${incident?.displayId || decodedIncidentId || 'the selected incident'}.`}
      actions={
        <button type="button" className="ui-button ui-button--ghost" onClick={handleBack}>
          Back
        </button>
      }
      scrollMode="content">
      {error ? <p className="org-shell__banner">{error.message}</p> : null}
      {loading && !incident ? <p className="org-shell__banner">Loading incident…</p> : null}
      {incident ? (
        <div className="unified-incident-room">
          <section className="incident-room__hero">
            <div className="incident-room__hero-copy">
              <span className="incident-room__eyebrow">{incident.displayId || 'Incident'}</span>
              <h2 className="incident-room__headline">{buildUnifiedIncidentHeadline(incident)}</h2>
              {incidentReferenceLine ? <p className="incident-room__hero-reference">{incidentReferenceLine}</p> : null}
            </div>
            <div className="incident-room__hero-badges">
              <span className="ui-badge ui-badge--ghost">{incident.source || '—'}</span>
              <span className={tokenBadgeClass()}>{normalizeEmpty(incident.priorityNormalized)}</span>
              <span className={tokenBadgeClass()}>{normalizeEmpty(incident.reapStatus)}</span>
              {incidentSeverity ? <span className={signalSeverityBadgeClass(incidentSeverity)}>{incidentSeverity.toUpperCase()}</span> : null}
              {incidentStatus ? <span className={signalStatusBadgeClass(incidentStatus)}>{incidentStatus.toUpperCase()}</span> : null}
              {subtypeLabel ? <span className="ui-badge ui-badge--ghost">{subtypeLabel}</span> : null}
            </div>
          </section>

          <div className="incident-room__kpis kpi-grid">
            <KpiCard label="Source" value={incident.source || '—'} secondary={incident.displayId || 'incident'} />
            <KpiCard label="ITSM State" value={incident.itsmStateNormalized || '—'} secondary="normalized" />
            <KpiCard label="Priority" value={incident.priorityNormalized || '—'} secondary={incident.reapConfidence || 'confidence n/a'} />
            <KpiCard
              label="Assignee"
              value={incident.assigneeUser || 'Unassigned'}
              secondary={incident.assigneeTeam || incident.ciOrAffectedService || 'no owner mapped'}
            />
            <KpiCard label="Updated" value={formatDateTime(incident.updatedAt)} secondary="source snapshot" />
            {incidentSeverity ? <KpiCard label="Severity" value={incidentSeverity} secondary="incident" /> : null}
            {incidentStatus ? <KpiCard label="Status" value={incidentStatus} secondary="lifecycle" /> : null}
            {signalDetail?.scopeType || signalDetail?.scopeId ? (
              <KpiCard label="Scope" value={signalDetail?.scopeType || '—'} secondary={signalDetail?.scopeId || '—'} />
            ) : null}
            {incidentTargetId ? <KpiCard label="Target" value={targetLabel} secondary={incidentTargetId} /> : null}
          </div>

          {incident.externalTickets && incident.externalTickets.length > 0 ? (
            <section className="section-card">
              <div className="section-card__header">
                <div>
                  <h3 className="section-card__title">External tickets</h3>
                  <p className="section-card__subtitle">Linked ITSM/provider tickets for this canonical incident.</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {incident.externalTickets.map((ticket: UnifiedIncidentExternalTicket, index: number) => {
                  const label = formatExternalTicketLabel(ticket);
                  const href = safeExternalTicketHref(ticket);
                  return href ? (
                    <a key={`${label}-${index}`} className="ui-badge ui-badge--ghost" href={href} target="_blank" rel="noreferrer">
                      {label}
                    </a>
                  ) : (
                    <span key={`${label}-${index}`} className="ui-badge ui-badge--ghost">
                      {label}
                    </span>
                  );
                })}
              </div>
            </section>
          ) : null}

          <div className="unified-incident-room__workspace">
            <div className="unified-incident-room__main">
              <section className="section-card">
                <div className="section-card__header">
                  <div>
                    <h3 className="section-card__title">Description</h3>
                    <p className="section-card__subtitle">Canonical incident description with richer incident context and operator guidance.</p>
                  </div>
                </div>
                <IncidentAssessment description={incident.description} analysis={signalDetail?.analysis ?? null} />
              </section>

              {signalDetail?.analysis ? (
                <section className="incident-room__panel">
                  <h3 className="incident-room__section-title">Analysis</h3>
                  <IncidentAnalysisPanel analysis={signalDetail.analysis} />
                </section>
              ) : null}

              {signalContextAvailable ? (
                <div className="incident-room__grid">
                  <section className="incident-room__panel">
                    <h3 className="incident-room__section-title">Topology Context</h3>
                    {!topologyIntent ? (
                      <p className="signals-summary__empty">No topology context was provided for this incident.</p>
                    ) : topologyQuery.loading && !topologyGraph ? (
                      <p className="org-shell__banner">Loading topology context…</p>
                    ) : topologyQuery.backendMissing ? (
                      <p className="signals-summary__empty">Topology context is not available in this environment yet.</p>
                    ) : topologyQuery.error ? (
                      <p className="signals-summary__empty">Topology context could not be resolved for this incident.</p>
                    ) : !topologyGraph || (topologyGraph.nodes ?? []).length === 0 ? (
                      <p className="signals-summary__empty">No topology graph data available.</p>
                    ) : (
                      <div className="incident-topology-graph">
                        <TopologyGraphRenderer
                          topology={topologyGraph}
                          graphKey={topologyGraphKey}
                          selectedNodeIds={topologySelectedNodeIds}
                          showControls={false}
                        />
                      </div>
                    )}
                  </section>

                  <section className="incident-room__panel">
                    <h3 className="incident-room__section-title">Telemetry Evidence</h3>
                    {signalDetailQuery.loading && !signalDetail ? (
                      <p className="org-shell__banner">Loading telemetry context…</p>
                    ) : telemetryWidgets.length === 0 ? (
                      <p className="signals-summary__empty">No curated telemetry dashboards are configured for this incident yet.</p>
                    ) : (
                      <div className="incident-room__telemetry-grid">
                        {telemetryWidgets.map((widget) => (
                          <GrafanaIframeCard
                            key={widget.id}
                            widget={widget}
                            height={260}
                            refreshSeconds={30}
                            timeRangePreset="1h"
                            onAuthRefresh={refreshGrafanaEmbedSession}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              ) : null}

              <section className="section-card">
                <div className="section-card__header">
                  <div>
                    <h3 className="section-card__title">Attachments</h3>
                    <p className="section-card__subtitle">Provider attachment inventory for the secondary unified incident route.</p>
                  </div>
                </div>
                {incident.attachments && incident.attachments.length > 0 ? (
                  <div className="unified-incident-attachments">
                    {incident.attachments.map((attachment: UnifiedIncidentAttachment) => {
                      const safeSourceUrl = sanitizeExternalLink(attachment.sourceUrl);
                      return (
                        <article key={attachment.id} className="unified-incident-attachments__item">
                          <div>
                            <div className="unified-incident-attachments__name">{attachment.name}</div>
                            <div className="unified-incident-attachments__meta">
                              {attachment.author ? <span>{attachment.author}</span> : null}
                              {attachment.sourceCreatedAt ? <span>{formatDateTime(attachment.sourceCreatedAt)}</span> : null}
                              {attachment.mimeType ? <span>{attachment.mimeType}</span> : null}
                            </div>
                          </div>
                          {safeSourceUrl ? (
                            <a className="ui-button ui-button--ghost" href={safeSourceUrl} target="_blank" rel="noreferrer">
                              Open provider source
                            </a>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="unified-incident-room__empty">No source attachments were synced for this incident.</p>
                )}
              </section>
            </div>

            <aside className="unified-incident-room__rail">
              <IncidentConversationPanel threadId={incident.threadId} />
            </aside>
          </div>
        </div>
      ) : null}
    </OrgModuleLayout>
  );
};

const IncidentAssessment = ({ description, analysis }: { description?: string; analysis?: SignalIncidentAnalysis | null }) => {
  const text = stripIncidentDescriptionMetadata(description);
  const parsed = parseAssessmentDescription(text);
  const sections = mergeAssessmentSections(parsed.sections, analysis);
  const intro = parsed.intro.length > 0 ? parsed.intro : analysis?.summary?.trim() ? [analysis.summary.trim()] : [];

  if (intro.length === 0 && sections.length === 0) {
    return <p className="unified-incident-room__empty">No description available.</p>;
  }

  if (sections.length === 0 && text) {
    return <p className="unified-incident-room__description">{text}</p>;
  }

  return (
    <div className="unified-incident-assessment">
      {intro.length > 0 ? <p className="unified-incident-assessment__intro">{intro.join(' ')}</p> : null}
      {sections.map((section) => (
        <section key={section.key} className="unified-incident-assessment__section">
          <h4 className="unified-incident-assessment__title">{section.heading}</h4>
          {section.key === 'next' ? (
            <ol className="unified-incident-assessment__steps">
              {toStepList(section.lines).map((step) => <li key={step}>{step}</li>)}
            </ol>
          ) : (
            <p className="unified-incident-assessment__body">{section.lines.join(' ').trim()}</p>
          )}
        </section>
      ))}
    </div>
  );
};

const IncidentAnalysisPanel = ({ analysis }: { analysis: SignalIncidentAnalysis }) => {
  const rationale = Array.isArray(analysis.rationale) ? analysis.rationale.filter(Boolean) : [];
  const nextChecks = Array.isArray(analysis.nextChecks) ? analysis.nextChecks.filter(Boolean) : [];

  return (
    <div className="detail-stack">
      <p>{analysis.summary?.trim() || '—'}</p>
      {analysis.impact?.trim() ? (
        <p>
          <strong>Likely impact:</strong> {analysis.impact.trim()}
        </p>
      ) : null}
      {rationale.length > 0 ? (
        <div>
          <strong>Why REAP thinks this</strong>
          <ul>
            {rationale.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {nextChecks.length > 0 ? (
        <div>
          <strong>What to check next</strong>
          <ul>
            {nextChecks.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const mergeAssessmentSections = (descriptionSections: AssessmentSection[], analysis?: SignalIncidentAnalysis | null): AssessmentSection[] => {
  const sectionMap = new Map<AssessmentSectionKey, AssessmentSection>();
  descriptionSections.forEach((section) => {
    if (section.lines.length > 0) {
      sectionMap.set(section.key, section);
    }
  });

  const addIfMissing = (key: AssessmentSectionKey, lines: string[]) => {
    if (sectionMap.has(key) || lines.length === 0) {
      return;
    }
    sectionMap.set(key, {
      key,
      heading: ASSESSMENT_SECTION_TITLES[key],
      lines
    });
  };

  addIfMissing('why', Array.isArray(analysis?.rationale) ? analysis.rationale.filter(Boolean) : []);
  addIfMissing('impact', analysis?.impact?.trim() ? [analysis.impact.trim()] : []);
  addIfMissing('next', Array.isArray(analysis?.nextChecks) ? analysis.nextChecks.filter(Boolean) : []);

  const orderedKeys: AssessmentSectionKey[] = ['what', 'why', 'impact', 'next'];
  return orderedKeys.map((key) => sectionMap.get(key)).filter((section): section is AssessmentSection => Boolean(section));
};

const toStepList = (lines: string[]): string[] =>
  lines
    .join('\n')
    .split(/\n(?=\s*(?:\d+\.\s+|[-*]\s+))/)
    .map((step) => step.replace(/^\s*(?:\d+\.\s+|[-*]\s+)/, '').trim())
    .filter(Boolean);

const getErrorStatus = (error: Error | null): number | null => {
  if (!error || !('response' in error)) {
    return null;
  }
  const response = (error as Error & { response?: unknown }).response;
  return response instanceof Response ? response.status : null;
};

const normalizeEmpty = (value?: string) => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : '—';
};

const firstPresentText = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = (value ?? '').trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
};

const sanitizeExternalLink = (value?: string): string | null => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch {
    return null;
  }
  return null;
};

const tokenBadgeClass = (): string => {
  return 'ui-badge ui-badge--ghost';
};

export default UnifiedIncidentPage;
